import { DropdownItem, DropdownMenu } from 'reactstrap';
import { useAtomValue } from 'jotai';
import classes from './ThemeMenu.module.scss';
import LnDropDownMenu from '../LnDropDownMenu';
import { useI18n } from 'src/utils/lni18n';
import { APP_THEMES, AUTO_THEME, DARK_THEME, LIGHT_THEME, themeService, themeStateAtom } from 'src/services/ThemeService';

/**
 * Bootstrap Icons paths (brightness-high / moon-fill / circle-half), inlined so they take
 * `currentColor` and follow the top menu's own palette. A theme with no entry here falls
 * back to the half-circle, so adding a colour mode needs no change in this file.
 */
const THEME_ICON_PATHS: Record<string, string> = {
    [LIGHT_THEME]:
        "M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6m0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8M8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 " +
        "0m0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13m8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5M3 " +
        "8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8m10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 " +
        "1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0m-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 " +
        "1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0m9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 " +
        "1.414a.5.5 0 0 1 0 .707M4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708",
    [DARK_THEME]:
        "M6 .278a.77.77 0 0 1 .08.858 7.2 7.2 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.79.79 0 0 1 " +
        ".81.316.73.73 0 0 1-.031.893A8.35 8.35 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.75.75 0 0 1 " +
        "6 .278",
    [AUTO_THEME]: "M8 15A7 7 0 1 0 8 1zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16",
};

const ThemeIcon = ({ theme }: { theme: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
        aria-hidden="true"
    >
        <path d={THEME_ICON_PATHS[theme] ?? THEME_ICON_PATHS[AUTO_THEME]} />
    </svg>
);

/**
 * Switches the app's colour mode. Renders straight off the theme registry in
 * `src/services/ThemeService`, so a newly registered mode shows up here on its own.
 */
const ThemeMenu = (props: { container?: HTMLElement | string | null }) => {
    const { preference, resolved } = useAtomValue(themeStateAtom);
    const { languageService: t } = useI18n();

    const container = props.container || "body";

    // The texts come from the server, which may not carry these keys yet; getText hands
    // back the key itself when it has nothing, so fall back to the built-in label.
    const labelFor = (key: string, fallback: string) => {
        const text = t.getText(key);
        return text === key ? fallback : text;
    };

    return (
        <LnDropDownMenu
            caret={false}
            toggleComponent={
                // The icon shows the mode in effect, so `auto` reads as what it resolved to.
                <span aria-label={labelFor("theme.switch", "Colour theme")}>
                    <ThemeIcon theme={preference === AUTO_THEME ? resolved : preference} />
                </span>
            }
            toogleClassName={[classes.toggle, "btn btn-minimal"].join(" ")}
            // A real <button>, unlike the neighbouring menus' <div>, so the switcher is
            // reachable and operable from the keyboard.
            toogleTagName="button"
        >
            <DropdownMenu className={classes.dropDownMenu} end container={container}>
                {APP_THEMES.map(({ id, labelKey, fallbackLabel }) => (
                    <DropdownItem
                        key={id}
                        id={`theme-option-${id}`}
                        active={id === preference}
                        onClick={() => themeService.setTheme(id)}
                        className={classes.themeItem}
                        tag="div"
                    >
                        <ThemeIcon theme={id} />
                        <span className="ms-2">{labelFor(labelKey, fallbackLabel)}</span>
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </LnDropDownMenu>
    );
};

export default ThemeMenu;
